import React from "react";

const ClassicBusiness = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-16 font-serif text-slate-900 border-[1px] border-slate-200 min-h-[1100px] flex flex-col justify-between shadow-2xl shadow-slate-100">
            <div>
                {/* Formal Header */}
                <div className="flex justify-between items-start border-b-[6px] border-slate-900 pb-12 mb-16">
                    <div className="space-y-6">
                        {companyDetails?.logo && (
                            <img src={companyDetails.logo} alt="Logo" className="h-16 w-auto grayscale contrast-125" />
                        )}
                        <div className="space-y-1">
                            <h2 className="text-3xl font-black uppercase tracking-tight text-slate-900">{companyDetails?.name}</h2>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest leading-loose max-w-sm">{companyDetails?.address}</p>
                        </div>
                    </div>

                    <div className="text-right space-y-6">
                        <h1 className="text-6xl font-black uppercase tracking-widest text-slate-100 italic leading-none mb-4 -mr-10">INVOICE</h1>
                        <div className="space-y-2 border-r-4 border-slate-900 pr-6">
                            <div className="text-lg font-black text-slate-900 uppercase">Ref No: {title}</div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none italic whitespace-nowrap overflow-hidden text-ellipsis">Official Financial Document</div>
                        </div>
                    </div>
                </div>

                {/* Information Grid */}
                <div className="grid grid-cols-2 gap-20 mb-20">
                    <div className="space-y-6">
                        <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 border-b border-slate-100 pb-2">Client Recipient</h4>
                        <div className="space-y-2">
                            <div className="text-2xl font-black text-slate-900 italic tracking-tight">{to}</div>
                            <div className="text-sm font-medium text-slate-500 italic max-w-xs">{clientAddress}</div>
                            <div className="flex flex-col pt-4 space-y-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                                {clientPhone && <span>Tel: {clientPhone}</span>}
                                {clientEmail && <span>Email: {clientEmail}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 bg-slate-50 p-8 rounded-2xl border border-slate-100 italic shadow-inner">
                        <div className="grid grid-cols-2 gap-4 text-[10px] font-black uppercase tracking-widest text-slate-400 leading-none">
                            <div className="items-center flex gap-2">Issue Date</div>
                            <div className="text-slate-900 border-b border-slate-200 pb-1">{date}</div>
                            <div className="items-center flex gap-2">Due Date</div>
                            <div className="text-rose-500 border-b border-slate-200 pb-1">{dueDate}</div>
                            <div className="items-center flex gap-2">Method</div>
                            <div className="text-slate-900 border-b border-slate-200 pb-1 whitespace-nowrap overflow-hidden text-ellipsis">Wire Transfer</div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="mb-20">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-900 text-white font-black uppercase text-[10px] tracking-widest">
                                <th className="p-6">Item Description</th>
                                <th className="p-6 text-center">Qty</th>
                                <th className="p-6 text-right">Rate</th>
                                <th className="p-6 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y-2 divide-slate-100">
                            {(invoiceItems || []).map((item, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                    <td className="p-8">
                                        <div className="text-lg font-black text-slate-900 italic tracking-tight uppercase leading-none">{item.item}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-3 font-bold">{item.description || "Standard Business Service"}</div>
                                    </td>
                                    <td className="p-8 text-center text-xs font-black text-slate-400 italic leading-none">{item.qty}</td>
                                    <td className="p-8 text-right text-xs font-black text-slate-900 italic tracking-tighter leading-none">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                    <td className="p-8 text-right text-lg font-black text-slate-900 italic tracking-tighter leading-none">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Section */}
            <div>
                <div className="flex justify-between items-start mb-16 border-t-2 border-slate-100 pt-16">
                    <div className="max-w-[400px] space-y-6">
                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">Notes & Terms</h4>
                            <p className="text-xs font-medium text-slate-400 tracking-widest leading-loose italic uppercase">
                                {notes || "We appreciate your continued interest in our products and services. Providing excellence is our commitment."}
                                <br /><br />
                                {terms || "Payment is strictly required within 30 days of receipt. Interest applicable on overdue balances."}
                            </p>
                        </div>

                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300 pt-6">Payable To</h4>
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 text-[10px] space-y-2 font-black uppercase tracking-widest text-slate-500 italic">
                                <div className="text-slate-900 border-b border-white pb-1">{companyDetails?.bankName || "Authorized Financial Institute"}</div>
                                <div className="text-slate-900 uppercase">Account No: {companyDetails?.accountNo || "-"}</div>
                            </div>
                        </div>
                    </div>

                    <div className="w-80 space-y-4">
                        <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-slate-300 italic border-b border-slate-50 pb-2">
                            <span>Subtotal</span>
                            <span className="text-slate-900">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-slate-300 italic border-b border-slate-50 pb-2">
                            <span>Taxation ({taxRate}%)</span>
                            <span className="text-slate-900">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-rose-300 italic border-b border-slate-50 pb-2">
                                <span>Discount</span>
                                <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                            </div>
                        )}
                        <div className="pt-10 flex justify-between items-end border-b-4 border-slate-900 pb-2">
                            <span className="text-xs font-black uppercase tracking-[0.3em] text-slate-900 italic">Balance Due</span>
                            <div className="text-5xl font-black text-slate-900 tracking-tighter italic leading-none">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-[0.4em] text-slate-200">
                    <div>Official Business Invoice</div>
                    <div className="italic text-slate-300">Generated via NexrBook Pro Edition</div>
                </div>
            </div>
        </div>
    );
};

export default ClassicBusiness;
